import {
  Button,
  Checkbox,
  Divider,
  Group,
  NumberInput,
  PasswordInput,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { IconBrandWindows } from '@tabler/icons-react';
import { RichTextArea } from 'components/RichTextArea';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { isDefined, isNonNullish } from 'remeda';
import { redirectTo } from 'shared/utils/redirect-to';
import { z } from 'zod/v4';
import {
  type ExternalMailboxConfiguration,
  type UpdateExternalMailboxConfigurationBody,
  useCreateExternalMailboxConfiguration,
  useLoadMicrosoftSharedMailboxLoginUri,
  useUpdateExternalMailboxConfiguration,
} from './api';

type ExternalMailboxFormValues = {
  host: string;
  // NumberInput hands back an empty string once the field is cleared.
  port: number | string;
  ssl: boolean;
  login: string;
  password: string;
  signature: string;
};

type ExternalMailboxFormProps = {
  mailbox?: ExternalMailboxConfiguration;
  onClose: VoidFunction;
};

const DEFAULT_IMAP_PORT = 993;

type MicrosoftMailboxKind = 'own' | 'shared';

// A Microsoft mailbox owns its own credentials and host: its signature is the only
// thing Recovr can edit. An existing IMAP mailbox keeps its stored password unless a
// new one is typed, so the field is only mandatory when creating one.
const buildValidationSchema = ({
  isMicrosoft,
  isEdit,
  requiredMessage,
}: {
  isMicrosoft: boolean;
  isEdit: boolean;
  requiredMessage: string;
}) => {
  if (isMicrosoft) return z.object({ signature: z.string() });

  return z.object({
    host: z.string().min(1, { error: requiredMessage }),
    port: z.coerce
      .number({ error: requiredMessage })
      .int({ error: requiredMessage })
      .positive({ error: requiredMessage }),
    ssl: z.boolean(),
    login: z.string().min(1, { error: requiredMessage }),
    password: isEdit ? z.string() : z.string().min(1, { error: requiredMessage }),
    signature: z.string(),
  });
};

const toUpdateBody = (
  values: ExternalMailboxFormValues,
  isMicrosoft: boolean,
): UpdateExternalMailboxConfigurationBody => {
  if (isMicrosoft) return { signature: values.signature };

  return {
    host: values.host,
    port: Number(values.port),
    ssl: values.ssl,
    login: values.login,
    signature: values.signature,
    // Omitting the password is what tells the backend to keep the stored one.
    ...(values.password.length > 0 ? { password: values.password } : {}),
  };
};

export const ExternalMailboxForm = ({ mailbox, onClose }: ExternalMailboxFormProps) => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();

  const isEdit = isDefined(mailbox);
  const isMicrosoft = mailbox?.isMicrosoft ?? false;
  const microsoftLoginUri = company.microsoft_login_uri;
  const [mailboxKind, setMailboxKind] = useState<MicrosoftMailboxKind>('own');
  const [sharedMailbox, setSharedMailbox] = useState('');
  const isSharedMailboxValid = z.email().safeParse(sharedMailbox).success;

  const { createExternalMailboxConfiguration, isCreateExternalMailboxConfigurationLoading } =
    useCreateExternalMailboxConfiguration();
  const { updateExternalMailboxConfiguration, isUpdateExternalMailboxConfigurationLoading } =
    useUpdateExternalMailboxConfiguration();
  const { loadMicrosoftSharedMailboxLoginUri, isLoadMicrosoftSharedMailboxLoginUriLoading } =
    useLoadMicrosoftSharedMailboxLoginUri();

  const form = useForm<ExternalMailboxFormValues>({
    initialValues: {
      host: mailbox?.host ?? '',
      port: mailbox?.port ?? DEFAULT_IMAP_PORT,
      ssl: mailbox?.ssl ?? true,
      login: mailbox?.login ?? '',
      password: '',
      signature: mailbox?.signature ?? '',
    },
    validate: schemaResolver(
      buildValidationSchema({
        isMicrosoft,
        isEdit,
        requiredMessage: t(i18nKeys.ERROR.FIELD_REQUIRED),
      }),
      { sync: true },
    ),
  });

  const handleSubmit = form.onSubmit(values => {
    if (isDefined(mailbox)) {
      updateExternalMailboxConfiguration(
        { id: mailbox.id, data: toUpdateBody(values, mailbox.isMicrosoft) },
        { onSuccess: onClose },
      );
      return;
    }

    createExternalMailboxConfiguration(
      {
        host: values.host,
        port: Number(values.port),
        ssl: values.ssl,
        login: values.login,
        password: values.password,
        signature: values.signature,
      },
      { onSuccess: onClose },
    );
  });

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        {!isEdit && isNonNullish(microsoftLoginUri) && (
          <>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                {t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.QUICK_SETUP)}
              </Text>
              <SegmentedControl<MicrosoftMailboxKind>
                value={mailboxKind}
                onChange={setMailboxKind}
                data={[
                  { value: 'own', label: t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.KIND.OWN) },
                  { value: 'shared', label: t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.KIND.SHARED) },
                ]}
              />
              {mailboxKind === 'shared' ? (
                <>
                  <TextInput
                    label={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.SHARED_ADDRESS)}
                    description={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.SHARED_ADDRESS_HINT)}
                    inputWrapperOrder={['label', 'input', 'description', 'error']}
                    withAsterisk
                    value={sharedMailbox}
                    onChange={event => setSharedMailbox(event.currentTarget.value)}
                  />
                  <Button
                    variant="default"
                    leftSection={<IconBrandWindows size={18} stroke={1.5} />}
                    disabled={!isSharedMailboxValid}
                    loading={isLoadMicrosoftSharedMailboxLoginUriLoading}
                    onClick={() =>
                      loadMicrosoftSharedMailboxLoginUri(
                        { sharedMailbox },
                        { onSuccess: loginUri => redirectTo(loginUri) },
                      )
                    }
                  >
                    {t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.SIGN_IN_WITH.MICROSOFT_SHARED)}
                  </Button>
                </>
              ) : (
                <Button
                  variant="default"
                  leftSection={<IconBrandWindows size={18} stroke={1.5} />}
                  onClick={() => redirectTo(microsoftLoginUri)}
                >
                  {t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.SIGN_IN_WITH.MICROSOFT)}
                </Button>
              )}
            </Stack>
            <Divider
              label={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.MANUAL_SETUP)}
              labelPosition="center"
            />
          </>
        )}

        {!isMicrosoft && (
          <>
            <TextInput
              label={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.LOGIN)}
              withAsterisk
              data-autofocus
              {...form.getInputProps('login')}
            />
            <PasswordInput
              label={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.PASSWORD)}
              withAsterisk={!isEdit}
              description={isEdit ? t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.PASSWORD_HINT) : undefined}
              {...form.getInputProps('password')}
            />
            <Group grow align="flex-start">
              <TextInput
                label={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.HOST)}
                withAsterisk
                {...form.getInputProps('host')}
              />
              <NumberInput
                label={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.PORT)}
                withAsterisk
                allowDecimal={false}
                allowNegative={false}
                {...form.getInputProps('port')}
              />
            </Group>
            <Checkbox
              label={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.SSL)}
              {...form.getInputProps('ssl', { type: 'checkbox' })}
            />
          </>
        )}

        <Stack gap={4}>
          <Text size="sm" fw={500}>
            {t(i18nKeys.SIGNATURE)}
          </Text>
          <RichTextArea
            content={mailbox?.signature ?? ''}
            onChange={content => form.setFieldValue('signature', content)}
            styles={{ content: { minHeight: 240 } }}
          />
        </Stack>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {t(i18nKeys.CANCEL)}
          </Button>
          <Button
            type="submit"
            disabled={!form.isDirty()}
            loading={
              isCreateExternalMailboxConfigurationLoading ||
              isUpdateExternalMailboxConfigurationLoading
            }
          >
            {t(i18nKeys.SAVE)}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
